// src/pages/Bookings.js
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { getBookingStats, updateBooking } from '../lib/airtable';
import Nav from '../components/Nav';
import Sidebar from '../components/Sidebar';

const STATUS_OPTIONS = ['New', 'Confirmed', 'Completed', 'Cancelled'];
const STATUS_STYLES = {
  New:       { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  Confirmed: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  Completed: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
  Cancelled: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
};

// Build a universal calendar link (works with Google Cal, Outlook, Apple)
function buildCalendarLink(b, businessName) {
  if (!b['Assigned Date']) return null;
  const dateStr = b['Assigned Date'];
  const timeStr = b['Assigned Time'] || '09:00';
  // Try to parse into a Date
  let start = new Date(`${dateStr}T${timeStr.includes(':') ? timeStr : timeStr + ':00'}`);
  if (isNaN(start.getTime())) start = new Date(dateStr);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000); // 2 hour default
  const fmt = d => d.toISOString().replace(/-|:|\.\d+/g, '');
  const title = encodeURIComponent(`${b['Service Type'] || 'Booking'} — ${b['Customer Name'] || 'Customer'}`);
  const details = encodeURIComponent(
    `Customer: ${b['Customer Name'] || ''}\nPhone: ${b['Phone'] || ''}\nEmail: ${b['Email'] || ''}\nService: ${b['Service Type'] || ''}\nAddress: ${b['Service Address'] || ''}\nNotes: ${b['Special Notes'] || ''}\n\nBooked via RingXai`
  );
  const location = encodeURIComponent(b['Service Address'] || '');
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}&location=${location}`;
}

// Editable cell — click to edit, blur or Enter to save
function EditableCell({ value, onSave, type = 'text', placeholder = '—' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');

  useEffect(() => { setVal(value || ''); }, [value]);

  function handleBlur() {
    setEditing(false);
    if (val !== (value || '')) onSave(val);
  }

  if (editing) {
    return (
      <input
        type={type}
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
        style={{
          width: '100%', padding: '4px 8px', fontSize: 14,
          border: '1.5px solid var(--ring)', borderRadius: 6, outline: 'none',
          fontFamily: 'var(--font-body)', background: '#fff'
        }}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      style={{ cursor: 'pointer', display: 'inline-block', minWidth: 60, padding: '2px 0' }}
      title="Click to edit"
    >
      {value || <span style={{ color: 'var(--muted)' }}>{placeholder}</span>}
    </span>
  );
}

// Status dropdown
function StatusDropdown({ status, onChange }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.New;
  return (
    <select
      value={status || 'New'}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        borderRadius: 999,
        padding: '4px 26px 4px 10px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='${encodeURIComponent(s.color)}' fill='none' stroke-width='1.5' stroke-linecap='round'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
        fontFamily: 'var(--font-body)',
      }}
    >
      {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  );
}

// Detail modal
function DetailModal({ booking, business, onClose, onSave }) {
  const [b, setB] = useState(booking);
  if (!booking) return null;
  const calLink = buildCalendarLink(b, business?.['Business Name']);

  function field(name, label, type = 'text', long = false) {
    return (
      <div className="form-group" key={name}>
        <label className="form-label">{label}</label>
        {long ? (
          <textarea className="form-textarea" value={b[name] || ''} onChange={(e) => setB({ ...b, [name]: e.target.value })} />
        ) : (
          <input className="form-input" type={type} value={b[name] || ''} onChange={(e) => setB({ ...b, [name]: e.target.value })} />
        )}
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,10,15,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }} onClick={onClose}>
      <div className="card" style={{ maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800 }}>Booking details</h2>
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>Edit any field, then save.</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {field('Customer Name', 'Customer Name')}
          {field('Phone', 'Phone', 'tel')}
          {field('Email', 'Email', 'email')}
          {field('Service Type', 'Service Type')}
          {field('Service Address', 'Address')}
          {field('Zip Code', 'Zip')}
          {field('Assigned Date', 'Date', 'date')}
          {field('Assigned Time', 'Time')}
          {field('Quoted Price', 'AI Quoted Price ($)', 'number')}
        </div>

        <div style={{ marginTop: 8 }}>
          {field('Special Notes', 'Special Notes from Call', 'text', true)}
        </div>

        <div className="divider" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {calLink && b['Booking Status'] === 'Confirmed' ? (
            <a href={calLink} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">
              📅 Add to Calendar
            </a>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {b['Booking Status'] === 'Confirmed' ? 'No date set — add one to enable calendar' : 'Mark as Confirmed to enable calendar add'}
            </span>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => onSave(b)}>Save changes</button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function Bookings() {
  const { business } = useAuth();
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter'); // "month" filter from dashboard

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [openDetail, setOpenDetail] = useState(null);
  const [toast, setToast] = useState(null);

  // Track which bookings have been "seen" — anything not seen shows red dot
  const [seenIds, setSeenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('ringxai_seen_bookings') || '[]')); }
    catch { return new Set(); }
  });

  function markSeen(id) {
    setSeenIds(prev => {
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem('ringxai_seen_bookings', JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  function loadBookings() {
    if (!business?.['Business Name']) { setLoading(false); return; }
    setLoading(true);
    getBookingStats(business['Business Name'])
      .then(s => setBookings(s.allBookings))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadBookings(); }, [business]);

  async function saveStatus(id, newStatus) {
    try {
      await updateBooking(id, { 'Booking Status': newStatus });
      setBookings(prev => prev.map(b => b.id === id ? { ...b, 'Booking Status': newStatus } : b));
      flashToast(`Status updated to ${newStatus}`);
    } catch {
      flashToast('Could not save status', true);
    }
  }

  async function saveField(id, field, value) {
    try {
      const payload = field === 'Quoted Price' ? { [field]: Number(value) || null } : { [field]: value };
      await updateBooking(id, payload);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, [field]: payload[field] } : b));
      flashToast(`Saved`);
    } catch {
      flashToast('Could not save', true);
    }
  }

  async function saveDetail(updated) {
    const { id, ...rest } = updated;
    // Strip read-only / system fields and undefined values
    const writable = ['Customer Name', 'Phone', 'Email', 'Service Type', 'Service Address', 'Zip Code', 'Assigned Date', 'Assigned Time', 'Quoted Price', 'Special Notes', 'Booking Status'];
    const fields = {};
    writable.forEach(k => {
      if (rest[k] !== undefined) {
        fields[k] = k === 'Quoted Price' ? (Number(rest[k]) || null) : rest[k];
      }
    });
    try {
      await updateBooking(id, fields);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, ...fields } : b));
      setOpenDetail(null);
      flashToast('Booking updated');
    } catch {
      flashToast('Could not save changes', true);
    }
  }

  function flashToast(msg, err = false) {
    setToast({ msg, err });
    setTimeout(() => setToast(null), 2400);
  }

  // Filter logic
  const filtered = useMemo(() => {
    let list = bookings;
    if (filterParam === 'month') {
      const now = new Date();
      list = list.filter(b => {
        if (!b['Date Booked']) return false;
        const d = new Date(b['Date Booked']);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });
    }
    if (statusFilter !== 'All') list = list.filter(b => (b['Booking Status'] || 'New') === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(b =>
        (b['Customer Name'] || '').toLowerCase().includes(q) ||
        (b['Service Type'] || '').toLowerCase().includes(q) ||
        (b['Phone'] || '').includes(search) ||
        (b['Zip Code'] || '').includes(search)
      );
    }
    return list;
  }, [bookings, search, statusFilter, filterParam]);

  const unseenCount = bookings.filter(b => !seenIds.has(b.id) && (b['Booking Status'] || 'New') === 'New').length;

  return (
    <div className="page">
      <Nav />
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">

          <div className="fade-up" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800 }}>Bookings</h1>
                {unseenCount > 0 && (
                  <span style={{ background: 'var(--red)', color: '#fff', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                    {unseenCount} new
                  </span>
                )}
              </div>
              <p style={{ color: 'var(--muted)', fontSize: 15, marginTop: 4 }}>
                {filterParam === 'month' ? `Showing this month's bookings — ` : ''}
                {filtered.length} of {bookings.length} total
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select"
                style={{ maxWidth: 160, padding: '8px 12px', fontSize: 14 }}
              >
                <option value="All">All statuses</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input
                className="form-input"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ maxWidth: 240, padding: '8px 12px', fontSize: 14 }}
              />
            </div>
          </div>

          {/* Tip */}
          <div className="alert alert-info" style={{ marginBottom: 20, fontSize: 13 }}>
            💡 <strong>Tip:</strong> Click any cell to edit. Click <strong>More info</strong> to see all booking details and add to your calendar.
          </div>

          {loading ? (
            <div className="card" style={{ textAlign: 'center', padding: 60 }}>
              <div className="spinner spinner-dark" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: 'var(--muted)' }}>Loading bookings…</p>
            </div>
          ) : filtered.length > 0 ? (
            <div className="table-wrap fade-up-2">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Service</th>
                    <th>Address</th>
                    <th>Status</th>
                    <th className="hide-mobile">Date Booked</th>
                    <th style={{ textAlign: 'right' }}>More info</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(b => {
                    const isUnseen = !seenIds.has(b.id) && (b['Booking Status'] || 'New') === 'New';
                    return (
                      <tr key={b.id}>
                        <td>
                          {isUnseen && <span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--red)', borderRadius: '50%', boxShadow: '0 0 6px var(--red)' }} title="New booking" />}
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          <EditableCell value={b['Customer Name']} onSave={(v) => saveField(b.id, 'Customer Name', v)} />
                        </td>
                        <td style={{ fontSize: 13 }}>
                          <EditableCell value={b['Phone']} onSave={(v) => saveField(b.id, 'Phone', v)} type="tel" />
                        </td>
                        <td>
                          <EditableCell value={b['Service Type']} onSave={(v) => saveField(b.id, 'Service Type', v)} />
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <EditableCell value={b['Service Address']} onSave={(v) => saveField(b.id, 'Service Address', v)} />
                        </td>
                        <td>
                          <StatusDropdown
                            status={b['Booking Status']}
                            onChange={(v) => saveStatus(b.id, v)}
                          />
                        </td>
                        <td className="hide-mobile" style={{ color: 'var(--muted)', fontSize: 13 }}>
                          {b['Date Booked'] ? new Date(b['Date Booked']).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => { setOpenDetail(b); markSeen(b.id); }}
                            style={{ padding: '4px 10px', fontSize: 12 }}
                          >
                            More info →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card fade-up" style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 8 }}>
                {search || statusFilter !== 'All' ? 'No results' : 'No bookings yet'}
              </h3>
              <p style={{ color: 'var(--muted)', fontSize: 14 }}>
                {search || statusFilter !== 'All' ? 'Try clearing your filters.' : 'Bookings will appear here once Sophie starts taking calls.'}
              </p>
            </div>
          )}

        </main>
      </div>

      {/* Detail modal */}
      {openDetail && (
        <DetailModal
          booking={openDetail}
          business={business}
          onClose={() => setOpenDetail(null)}
          onSave={saveDetail}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
          background: toast.err ? 'var(--red)' : 'var(--ink)', color: '#fff',
          padding: '10px 18px', borderRadius: 10, fontSize: 14, fontWeight: 500,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
        }}>
          {toast.err ? '⚠️ ' : '✓ '}{toast.msg}
        </div>
      )}
    </div>
  );
}
